import { useState } from "react";
import { CheckCircle2, Loader2, Upload, X } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { TextField, TextArea, SelectField, CheckGroup, isEmail } from "@/components/forms/FormBits";
import { submitListing } from "@/lib/api";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { propertyTypes, serviceOptions, IMG } from "@/data/content";

const empty = {
  name: "", property_name: "", phone: "", whatsapp: "", email: "", address: "", city: "",
  property_type: "", rooms: "", units: "", website: "",
  booking_com: "", airbnb: "", agoda: "", expedia: "", google_profile: "", tripadvisor: "",
  description: "", additional: "",
};

const fileToDataUrl = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });

export default function ListProperty() {
  const [form, setForm] = useState(empty);
  const [services, setServices] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (v) => setServices((cur) => (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - photos.length);
    const valid = files.filter((f) => f.size <= 4 * 1024 * 1024);
    const urls = await Promise.all(valid.map(fileToDataUrl));
    setPhotos((cur) => [...cur, ...urls].slice(0, 6));
  };
  const removePhoto = (i) => setPhotos((cur) => cur.filter((_, idx) => idx !== i));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.property_name.trim()) e.property_name = "Please provide your property name.";
    if (!form.phone.trim()) e.phone = "Please enter a phone number.";
    if (!isEmail(form.email)) e.email = "Please enter a valid email address.";
    if (!form.city.trim()) e.city = "Please enter your city.";
    if (!form.property_type) e.property_type = "Please select a property type.";
    setErrors(e);
    if (Object.keys(e).length) window.scrollTo({ top: 300, behavior: "smooth" });
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      await submitListing({ ...form, services, photos });
      trackEvent(EVENTS.LISTING_SUBMITTED);
      setStatus("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <>
        <Seo title="Property Submitted | HM Services" description="Thank you for submitting your property to HM Services." path="/list-your-property" />
        <PageHero eyebrow="List Your Property" title="Ready to Put Your Property in Front of More Guests?" image={IMG.villaPool} />
        <section className="bg-white py-28">
          <div className="mx-auto max-w-xl px-5 text-center" data-testid="listing-success">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-ocean/10"><CheckCircle2 className="h-9 w-9 text-ocean" /></div>
            <h2 className="mt-6 font-serif text-3xl text-navy">Thank you!</h2>
            <p className="mt-4 text-slate-600">Your property details have been received. HM Services will review your information and contact you shortly to discuss the right platforms and services.</p>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Seo title="List Your Property | HM Services" description="Tell us about your hotel, guest house, resort or villa and HM Services will help you identify the right digital platforms and services for your business." path="/list-your-property" image={IMG.villaPool} />
      <PageHero eyebrow="List Your Property" title="Ready to Put Your Property in Front of More Guests?" subtitle="Tell us about your property and HM Services will help you identify the right digital platforms and services for your business." image={IMG.villaPool} />

      <section className="bg-mist py-24 lg:py-28" data-testid="listing-page">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal>
            <form onSubmit={onSubmit} className="space-y-10 rounded-3xl border border-line bg-white p-8 shadow-float-lg sm:p-10" data-testid="listing-form">
              {/* Contact */}
              <fieldset className="space-y-5">
                <legend className="font-serif text-xl font-semibold text-navy">Your Details</legend>
                <div className="grid gap-5 sm:grid-cols-2">
                  <TextField id="l-name" label="Owner / Manager Name" required value={form.name} onChange={set("name")} error={errors.name} data-testid="listing-name" />
                  <TextField id="l-property" label="Property Name" required value={form.property_name} onChange={set("property_name")} error={errors.property_name} data-testid="listing-property" />
                  <TextField id="l-phone" label="Phone" required value={form.phone} onChange={set("phone")} error={errors.phone} data-testid="listing-phone" />
                  <TextField id="l-whatsapp" label="WhatsApp" value={form.whatsapp} onChange={set("whatsapp")} placeholder="Optional" data-testid="listing-whatsapp" />
                  <TextField id="l-email" label="Email" required type="email" value={form.email} onChange={set("email")} error={errors.email} data-testid="listing-email" />
                  <TextField id="l-city" label="City" required value={form.city} onChange={set("city")} error={errors.city} data-testid="listing-city" />
                </div>
                <TextField id="l-address" label="Property Address" value={form.address} onChange={set("address")} placeholder="Optional" data-testid="listing-address" />
              </fieldset>

              {/* Property */}
              <fieldset className="space-y-5">
                <legend className="font-serif text-xl font-semibold text-navy">Property Information</legend>
                <div className="grid gap-5 sm:grid-cols-3">
                  <SelectField id="l-type" label="Property Type" required options={propertyTypes} value={form.property_type} onChange={set("property_type")} error={errors.property_type} placeholder="Select type" data-testid="listing-type" />
                  <TextField id="l-rooms" label="Number of Rooms" value={form.rooms} onChange={set("rooms")} placeholder="Optional" data-testid="listing-rooms" />
                  <TextField id="l-units" label="Number of Units" value={form.units} onChange={set("units")} placeholder="Optional" data-testid="listing-units" />
                </div>
                <TextField id="l-website" label="Current Website" value={form.website} onChange={set("website")} placeholder="Optional" data-testid="listing-website" />
                <TextArea id="l-desc" label="Property Description" rows={4} value={form.description} onChange={set("description")} placeholder="Describe your property, location and what makes it special..." data-testid="listing-description" />
              </fieldset>

              {/* Existing listings */}
              <fieldset className="space-y-5">
                <legend className="font-serif text-xl font-semibold text-navy">Existing Listings <span className="text-sm font-normal text-slate-400">(paste links if available)</span></legend>
                <div className="grid gap-5 sm:grid-cols-2">
                  <TextField id="l-booking" label="Booking.com Listing" value={form.booking_com} onChange={set("booking_com")} placeholder="Optional" data-testid="listing-booking" />
                  <TextField id="l-airbnb" label="Airbnb Listing" value={form.airbnb} onChange={set("airbnb")} placeholder="Optional" data-testid="listing-airbnb" />
                  <TextField id="l-agoda" label="Agoda Listing" value={form.agoda} onChange={set("agoda")} placeholder="Optional" data-testid="listing-agoda" />
                  <TextField id="l-expedia" label="Expedia Listing" value={form.expedia} onChange={set("expedia")} placeholder="Optional" data-testid="listing-expedia" />
                  <TextField id="l-google" label="Google Business Profile" value={form.google_profile} onChange={set("google_profile")} placeholder="Optional" data-testid="listing-google" />
                  <TextField id="l-tripadvisor" label="Tripadvisor" value={form.tripadvisor} onChange={set("tripadvisor")} placeholder="Optional" data-testid="listing-tripadvisor" />
                </div>
              </fieldset>

              {/* Services + photos */}
              <fieldset className="space-y-5">
                <legend className="font-serif text-xl font-semibold text-navy">What You Need</legend>
                <CheckGroup label="Services Needed" options={serviceOptions} values={services} onToggle={toggle} columns={2} testId="listing-services" />

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-navy">Upload Property Photos <span className="text-slate-400">(up to 6, max 4MB each)</span></label>
                  <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-line bg-mist/60 px-6 py-10 text-center transition-colors hover:border-ocean/50" data-testid="listing-photo-dropzone">
                    <Upload className="h-7 w-7 text-ocean" />
                    <span className="mt-3 text-sm font-medium text-navy">Click to upload photos</span>
                    <span className="mt-1 text-xs text-slate-500">JPG or PNG</span>
                    <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="listing-photo-input" />
                  </label>
                  {photos.length > 0 && (
                    <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-6" data-testid="listing-photo-previews">
                      {photos.map((p, i) => (
                        <div key={i} className="group relative aspect-square overflow-hidden rounded-xl border border-line">
                          <img src={p} alt={`Upload ${i + 1}`} className="h-full w-full object-cover" />
                          <button type="button" onClick={() => removePhoto(i)} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-navy/80 text-white opacity-0 transition-opacity group-hover:opacity-100" data-testid={`listing-photo-remove-${i}`} aria-label="Remove photo">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <TextArea id="l-additional" label="Additional Information" rows={3} value={form.additional} onChange={set("additional")} placeholder="Anything else we should know?" data-testid="listing-additional" />
              </fieldset>

              {status === "error" && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600" data-testid="listing-error">Something went wrong. Please try again.</p>}

              <Button type="submit" disabled={status === "loading"} className="w-full rounded-full bg-navy py-6 text-base text-white hover:bg-navy-800" data-testid="listing-submit">
                {status === "loading" ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</> : "Submit Property Details"}
              </Button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
